import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Typography,
} from '@mui/material';
import { InvoiceDetail, PortalApi } from './portalApi';
import { portalErrorMessage } from './portalHttp';
import { usePortalSession } from './PortalSession';
import { statusColor, useInvoiceActions } from './PortalInvoices';
import { formatMoney, humanizeEnum } from '../utils/format';
import { formatDate } from '../utils/dateUtils';

// ---- P1-B customer portal ----
const PortalInvoiceDetail: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const { code } = usePortalSession();
  const [inv, setInv] = useState<InvoiceDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const actions = useInvoiceActions();

  useEffect(() => {
    let cancelled = false;
    PortalApi.invoice(id)
      .then(d => { if (!cancelled) setInv(d); })
      .catch(e => { if (!cancelled) setError(portalErrorMessage(e, 'Could not load this invoice')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <CircularProgress />;
  if (!inv) return <Alert severity="error">{error ?? 'Not found'}</Alert>;
  const money = (n: number) => formatMoney(n, inv.currency);
  return (
    <Box>
      <Link component={RouterLink} to={`/portal/${encodeURIComponent(code)}`}>Back to invoices</Link>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between', my: 2 }}>
        <Typography variant="h5">{inv.number} <Chip size="small" label={humanizeEnum(inv.status)} color={statusColor(inv.status)} /></Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" disabled={actions.busyId === inv.id} onClick={() => actions.download(inv.id, inv.number)}>Download PDF</Button>
          {inv.payable && <Button variant="contained" disabled={actions.busyId === inv.id} onClick={() => actions.pay(inv.id)}>Pay now</Button>}
        </Box>
      </Box>
      {actions.error && <Alert severity="error" sx={{ mb: 2 }}>{actions.error}</Alert>}
      <Paper sx={{ p: 2, mb: 2, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 2 }}>
        <Box><Typography variant="body2" color="text.secondary">Issued</Typography><Typography>{formatDate(inv.issueDate)}</Typography></Box>
        <Box><Typography variant="body2" color="text.secondary">Due</Typography><Typography>{inv.dueDate ? formatDate(inv.dueDate) : '-'}</Typography></Box>
        <Box><Typography variant="body2" color="text.secondary">Total</Typography><Typography>{money(inv.total)}</Typography></Box>
        <Box><Typography variant="body2" color="text.secondary">Paid</Typography><Typography>{money(inv.paid)}</Typography></Box>
        <Box><Typography variant="body2" color="text.secondary">Credited</Typography><Typography>{money(inv.credited)}</Typography></Box>
        <Box><Typography variant="body2" color="text.secondary">Outstanding</Typography><Typography fontWeight="bold">{money(inv.outstanding)}</Typography></Box>
      </Paper>
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Description</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Unit price</TableCell>
              <TableCell align="right">Discount</TableCell><TableCell align="right">Tax</TableCell><TableCell align="right">Total</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {inv.lines.map((l, i) => (
                <TableRow key={i}>
                  <TableCell>{l.description}</TableCell><TableCell align="right">{l.quantity}</TableCell>
                  <TableCell align="right">{money(l.unitPrice)}</TableCell><TableCell align="right">{money(l.discount)}</TableCell>
                  <TableCell align="right">{money(l.tax)}</TableCell><TableCell align="right">{money(l.lineTotal)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default PortalInvoiceDetail;
// ---- end P1-B ----
